import { Fragment, useEffect, useState } from 'react';
import { DetailViewCard } from '../CardWithTitle';
import {
  Comment,
  getCommentLoadingSubject,
  getCommentSubject,
  fetchComments,
  createComment
} from '../../../store/Comment';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { CommentItem } from './CommentItem';
import { TextEditor } from './TextEditor';
import { TextSpan } from '../../Atoms/Text';
import { Icon } from '../../Atoms/Icon';
import { fetchUsersByRole, getUsersByRolesSubject, User, UserRole } from '../../../store/User';

export interface DetailsCommentsProps {
  id: string;
  entityType: string;
}

export function DetailsComments({ id, entityType }: DetailsCommentsProps) {
  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(true);
  const [commentToReply, setCommentToReply] = useState<Comment | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [mentionableUsers, setMentionableUsers] = useState<User[]>([]);

  useEffect(() => {
    const commentSub = getCommentSubject(id, entityType).subscribe((comments) => setComments(comments));
    const commentLoadingSub = getCommentLoadingSubject(id, entityType).subscribe((loading) => setIsFetching(loading));
    const mentionableUsersSub = getUsersByRolesSubject().subscribe((users) => setMentionableUsers(users));

    return () => {
      if (commentSub) commentSub.unsubscribe();
      if (commentLoadingSub) commentLoadingSub.unsubscribe();
      if (mentionableUsersSub) mentionableUsersSub.unsubscribe();
    };
  }, [id, entityType]);

  useEffect(() => {
    if (id) getData();
  }, [id, entityType]);

  const getData = async () => {
    setIsFetching(true);
    fetchComments(id, entityType);
    fetchUsersByRole([UserRole.ADMIN, UserRole.ENGINEER, UserRole.ANALYST, UserRole.DATA, UserRole.OBSERVER]);
    setIsFetching(false);
  };

  useEffect(() => {
    if (comments.length > 0) setCollapsed(false);
  }, [comments]);

  const cardActions = () => {
    return (
      <button
        className="flex items-center space-x-2"
        onClick={() => {
          setIsFormOpen(true);
          setCollapsed(false);
        }}
        data-cy="new-ticket-button"
        data-testid="new-ticket-button">
        <Icon type="plus" />
        <TextSpan
          color="indigo"
          size="sm14">
          New Comment
        </TextSpan>
      </button>
    );
  };

  const handleSend = async (commentText: string) => {
    setSending(true);
    await createComment({ entityId: id, entityType: entityType, commentText: commentText || '' });
    setIsFormOpen(false);
    setSending(false);
  };

  const handleCancel = () => {
    setIsFormOpen(false);
  };

  const commentTextWithMention = () => {
    if (commentToReply?.parent_comment_id) {
      return (
        '<p><span class="mention" data-index="1" data-denotation-char="@" data-id="' +
        `${commentToReply.createdBy?.email}" ` +
        `data-value="${commentToReply.createdBy?.firstName} ${commentToReply.createdBy?.lastName}"` +
        'contenteditable="false">' +
        `@${commentToReply.createdBy?.firstName} ${commentToReply.createdBy?.lastName}</span>&nbsp;</p>`
      );
    } else {
      return '';
    }
  };

  const renderComments = () => {
    return comments
      .filter((c) => !c.parent_comment_id)
      .map((comment) => (
        <Fragment key={comment.id}>
          <CommentItem
            comment={comment}
            handleReply={handleReply}
            mentionableUsers={mentionableUsers}
          />
          {comments
            .filter((r) => String(r.parent_comment_id) == comment.id)
            .reverse()
            .map((reply) => (
              <CommentItem
                comment={reply}
                handleReply={handleReply}
                key={reply.id}
                mentionableUsers={mentionableUsers}
              />
            ))}
          {isReplying && (commentToReply == comment || String(commentToReply?.parent_comment_id) == comment.id) && (
            <div className="mt-4 ml-6">
              <TextEditor
                commentText={commentTextWithMention()}
                handleCancel={handleCancelReply}
                handleSend={handleCreateReply}
                placeholder="Reply to this comment..."
                portalUsers={mentionableUsers}
              />
            </div>
          )}
        </Fragment>
      ));
  };

  const handleReply = (comment: Comment) => {
    setCommentToReply(comment);
    setIsReplying(true);
  };

  const handleCancelReply = () => {
    setCommentToReply(null);
    setIsReplying(false);
  };

  const handleCreateReply = (commentText: string) => {
    if (commentToReply) {
      createComment({
        entityId: commentToReply.entityId,
        entityType: commentToReply.entityType,
        commentText: commentText || '',
        parent_comment_id: commentToReply.parent_comment_id || commentToReply.id
      });
      setCommentToReply(null);
    }
    setIsReplying(false);
  };

  return (
    <DetailViewCard
      cardActions={cardActions()}
      collapsed={collapsed}
      enableCollapse
      key="comments_details"
      title={`Comments (${comments.filter((c) => !c.parent_comment_id)?.length || 0})`}
      childrenWrapperClass="flex p-6 overflow-x-auto max-h-96">
      <div className="font-sans text-black w-full">
        {(isFetching || sending) && <ComponentSpinner label={sending ? 'Processing' : 'Loading'} />}
        {!isFetching && !sending && isFormOpen && (
          <TextEditor
            commentText=""
            handleCancel={handleCancel}
            handleSend={handleSend}
            placeholder="Add a comment"
            portalUsers={mentionableUsers}
          />
        )}
        {!isFetching && !sending && !comments?.length && !isFormOpen && <TextSpan size="sm14">No comments</TextSpan>}
        {!isFetching && !sending && !!comments?.length && renderComments()}
      </div>
    </DetailViewCard>
  );
}
